/**
 * Formatting Utilities for PeopleTag
 * Functions for formatting data for display
 */

/**
 * Format wallet address for display
 * @param {string} address - Wallet address
 * @param {number} startChars - Number of characters to show at start
 * @param {number} endChars - Number of characters to show at end
 * @returns {string} Formatted address
 */
export const formatWalletAddress = (address, startChars = 6, endChars = 4) => {
  if (!address) return '';
  if (address.length <= startChars + endChars) return address;
  return `${address.slice(0, startChars)}...${address.slice(-endChars)}`;
};

/**
 * Format date for display
 * @param {Date|string|number} date - Date to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };

  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    return new Intl.DateTimeFormat('en-US', defaultOptions).format(dateObj);
  } catch (error) {
    return '';
  }
};

/**
 * Format relative time (e.g., "2 hours ago")
 * @param {Date|string|number} date - Date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return '';
  
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    
    const now = new Date();
    const diffMs = now - dateObj;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSecs < 60) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`;
    if (diffMonths < 12) return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
    return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`;
  } catch (error) {
    return '';
  }
};

/**
 * Format number with commas
 * @param {number} num - Number to format
 * @returns {string} Formatted number
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  return num.toLocaleString('en-US');
};

/**
 * Format file size
 * @param {number} bytes - Size in bytes
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Format distance
 * @param {number} distance - Distance in km
 * @param {string} unit - 'km' or 'mi'
 * @returns {string} Formatted distance
 */
export const formatDistance = (distance, unit = 'km') => {
  if (distance === null || distance === undefined) return 'Unknown';
  
  if (unit === 'mi') {
    const miles = distance * 0.621371;
    if (miles < 1) {
      return `${Math.round(miles * 5280)} ft`;
    }
    return `${miles.toFixed(1)} mi`;
  }
  
  if (distance < 1) {
    return `${Math.round(distance * 1000)} m`;
  }
  return `${distance.toFixed(1)} km`;
};

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @param {string} suffix - Suffix to add (default: '...')
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength, suffix = '...') => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - suffix.length) + suffix;
};

/**
 * Capitalize first letter of each word
 * @param {string} text - Text to capitalize
 * @returns {string} Capitalized text
 */
export const capitalizeWords = (text) => {
  if (!text) return '';
  return text.replace(/\b\w/g, char => char.toUpperCase());
};

/**
 * Format tag for display (remove special characters, capitalize)
 * @param {string} tag - Tag to format
 * @returns {string} Formatted tag
 */
export const formatTag = (tag) => {
  if (!tag) return '';
  return capitalizeWords(tag.trim().toLowerCase());
};

/**
 * Format social media URL for display
 * @param {string} url - Social media URL
 * @returns {string} Formatted platform name or URL
 */
export const formatSocialMediaUrl = (url) => {
  if (!url) return '';
  
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();
    
    // Extract platform name
    if (hostname.includes('twitter.com') || hostname.includes('x.com')) {
      return 'Twitter';
    } else if (hostname.includes('instagram.com')) {
      return 'Instagram';
    } else if (hostname.includes('facebook.com')) {
      return 'Facebook';
    } else if (hostname.includes('linkedin.com')) {
      return 'LinkedIn';
    } else if (hostname.includes('github.com')) {
      return 'GitHub';
    } else if (hostname.includes('youtube.com')) {
      return 'YouTube';
    } else if (hostname.includes('tiktok.com')) {
      return 'TikTok';
    } else if (hostname.includes('discord.com')) {
      return 'Discord';
    } else if (hostname.includes('telegram.org')) {
      return 'Telegram';
    }
    
    // Return domain name if no match
    return hostname.replace('www.', '');
  } catch (error) {
    return url;
  }
};

/**
 * Format percentage
 * @param {number} value - Value to format (0-100)
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage
 */
export const formatPercentage = (value, decimals = 0) => {
  if (value === null || value === undefined) return '0%';
  return `${value.toFixed(decimals)}%`;
};

/**
 * Format duration (e.g., "2h 30m")
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration
 */
export const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return '0s';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 && hours === 0) parts.push(`${secs}s`);
  
  return parts.join(' ') || '0s';
};

/**
 * Format list of items with proper grammar
 * @param {Array} items - Array of items
 * @param {string} conjunction - Conjunction to use (default: 'and')
 * @returns {string} Formatted list
 */
export const formatList = (items, conjunction = 'and') => {
  if (!items || items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} ${conjunction} ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, ${conjunction} ${items[items.length - 1]}`;
};

/**
 * Format username with @ symbol
 * @param {string} username - Username
 * @returns {string} Formatted username
 */
export const formatUsername = (username) => {
  if (!username) return '';
  return username.startsWith('@') ? username : `@${username}`;
};

/**
 * Format age range
 * @param {number} minAge - Minimum age
 * @param {number} maxAge - Maximum age
 * @returns {string} Formatted age range
 */
export const formatAgeRange = (minAge, maxAge) => {
  if (minAge === null || minAge === undefined) {
    if (maxAge === null || maxAge === undefined) return 'Any age';
    return `Up to ${maxAge}`;
  }
  if (maxAge === null || maxAge === undefined) {
    return `${minAge}+`;
  }
  if (minAge === maxAge) {
    return `${minAge}`;
  }
  return `${minAge}-${maxAge}`;
};

